import React, { useEffect, useState } from "react";
import "./CouponModal.css";
import { IoClose } from "react-icons/io5";
import { API_BASE_URL } from "../utils";

const CouponModal = ({ 
  show, 
  onClose, 
  availableCoupons = [], 
  onApplyCoupon, 
  selectedCoupon,
  orderAmount = 0 
}) => {
  const [manualInput, setManualInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Reset manual input when modal closes
  useEffect(() => {
    if (!show) {
      setManualInput("");
      setSearchTerm("");
    }
  }, [show]);

  // Set manual input to selected coupon code when a coupon is selected
  useEffect(() => {
    if (selectedCoupon) {
      setManualInput(selectedCoupon.code || "");
    }
  }, [selectedCoupon]);

  // Check if coupon is applicable
  const isCouponApplicable = (coupon) => {
    const minSpent = parseFloat(coupon.min_spent || 0);
    const isExpired = new Date(coupon.expiry_date) < new Date();
    return orderAmount >= minSpent && !isExpired;
  };

  // Calculate discount amount for display
  const getDiscountAmount = (coupon) => {
    if (!isCouponApplicable(coupon)) return 0;
    
    if (coupon.discount_type === 'percent') {
      let discount = (orderAmount * parseFloat(coupon.discount)) / 100;
      if (coupon.capping) {
        discount = Math.min(discount, parseFloat(coupon.capping));
      }
      return discount;
    } else {
      return parseFloat(coupon.discount);
    }
  };

  const formatDiscountText = (coupon) => {
    if (coupon.discount_type === 'percent') {
      return `${coupon.discount}% OFF${coupon.capping ? ` (Max ₹${coupon.capping})` : ''}`;
    } else {
      return `₹${coupon.discount} OFF`;
    }
  };

  const handleApplyCouponFromList = (coupon) => {
    if (isCouponApplicable(coupon)) {
      onApplyCoupon(coupon);
      onClose();
    }
  };

  const handleManualApply = () => {
    const inputCode = manualInput.trim().toUpperCase();
    if (inputCode !== "") {
      // Check if the manual code matches any available coupon
      const matchingCoupon = availableCoupons.find(
        coupon => coupon.code.toUpperCase() === inputCode
      );
      
      if (matchingCoupon) {
        if (isCouponApplicable(matchingCoupon)) {
          onApplyCoupon(matchingCoupon);
          onClose();
        } else {
          alert(`This coupon requires minimum spend of ₹${matchingCoupon.min_spent} or may be expired.`);
        }
      } else {
        alert("Invalid coupon code. Please check and try again.");
      }
    }
  };

  const handleRemoveCoupon = () => {
    onApplyCoupon(null);
    setManualInput("");
  };

  // Filter coupons based on search term
  const filteredCoupons = availableCoupons.filter(coupon =>
    coupon.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  console.log("Filtered Coupons:", filteredCoupons);

  if (!show) return null;

  return (
    <div className="coupon-modal-overlay" onClick={onClose}>
      <div className="coupon-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="coupon-modal-header">
          <h3>Apply Coupons</h3>
          <button onClick={onClose} className="coupon-close-btn">
            <IoClose color="#ff4444" size={24} />
          </button>
        </div>

        {/* Manual Input Section */}
        <div className="coupon-modal-input">
          <input
            type="text"
            placeholder="Enter coupon code"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value.toUpperCase())}
            className="coupon-input-field"
          />
          <button 
            className="coupon-apply-btn-input" 
            onClick={handleManualApply}
            disabled={!manualInput.trim()}
          >
            Apply
          </button>
        </div>

        {/* Search Section */}
        <div className="coupon-search-section">
          <input
            type="text"
            placeholder="Search available coupons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="coupon-search-input"
          />
        </div>

        {/* Selected Coupon Display */}
        {selectedCoupon && (
          <div className="coupon-selected-message">
            <div className="selected-coupon-info">
              <strong>Selected Coupon:</strong> {selectedCoupon.code}
              <span className="selected-discount">
                - Save ₹{getDiscountAmount(selectedCoupon).toFixed(2)}
              </span>
            </div>
            <button 
              className="remove-coupon-btn"
              onClick={handleRemoveCoupon}
            >
              Remove
            </button>
          </div>
        )}

        {/* Available Coupons List */}
        <div className="coupon-coupon-list">
          <h4 className="available-coupons-title">Available Coupons</h4>
          
          {filteredCoupons.length === 0 ? (
            <div className="no-coupon-coupon-card">
              <p>
                {searchTerm 
                  ? "No coupons found matching your search." 
                  : "No coupons available at the moment."
                }
              </p>
            </div>
          ) : (
            filteredCoupons.map((coupon) => {
              const isApplicable = isCouponApplicable(coupon);
              const discountAmount = getDiscountAmount(coupon);
              const isSelected = selectedCoupon?.id === coupon.id;
              const isExpired = new Date(coupon.expiry_date) < new Date();

              return (
                <div 
                  className={`coupon-coupon-card ${!isApplicable ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`} 
                  key={coupon.id}
                >
                  <div className="coupon-left-section">
                    {coupon.img && (
                      <div className="coupon-image-container">
                        <img 
                          src={`${API_BASE_URL}/master/assets/uploads/${coupon.img}`} 
                          alt="Coupon" 
                          className="coupon-image"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="coupon-coupon-details">
                      <h4 className="coupon-code-title">
                        {coupon.code}
                      </h4>
                      <p className="coupon-discount-text">
                        {formatDiscountText(coupon)}
                      </p>
                      {coupon.min_spent > 0 && (
                        <p className="coupon-condition">
                          Min spend: ₹{coupon.min_spent}
                        </p>
                      )}
                      <p className="coupon-expiry">
                        Valid till: {new Date(coupon.expiry_date).toLocaleDateString('en-IN')}
                      </p>
                      {coupon.applicable_for && (
                        <p className="coupon-applicable">
                          For {coupon.applicable_for} users
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="coupon-right-section">
                    {isExpired ? (
                      <span className="expired-tag">Expired</span>
                    ) : !isApplicable ? (
                      <div className="not-applicable-section">
                        <span className="not-applicable-tag">
                          Min ₹{coupon.min_spent} required
                        </span>
                      </div>
                    ) : (
                      <div className="coupon-actions">
                        <p className="save-amount">Save ₹{discountAmount.toFixed(2)}</p>
                        <button
                          className={`coupon-apply-btn ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleApplyCouponFromList(coupon)}
                          disabled={!isApplicable}
                        >
                          {isSelected ? 'Selected' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Terms Section */}
        {selectedCoupon?.terms_conditions && (
          <div className="coupon-terms-section">
            <h4>Terms & Conditions</h4>
            <div 
              className="terms-content"
              dangerouslySetInnerHTML={{ 
                __html: selectedCoupon.terms_conditions 
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponModal;